import { LitElement, html } from 'lit';
import styles from './studio-actions.css.mjs';

export class StudioActions extends LitElement {
  static styles = styles;

  static properties = {
    open: { type: Boolean, reflect: true },
    title: { type: String, reflect: true },
    message: { type: String, reflect: true },
    actions: { type: Array, reflect: true },
    footerButton: { type: Object, reflect: true },
  };

  constructor() {
    super();
    this.open = false;
    this.title = '';
    this.message = '';
    this.actions = [];
    this.footerButton = { text: 'Confirm' };
    this._eventListeners = [];
  }

  set actions(value) {
    this._actions = Array.isArray(value)
      ? value.filter(a => a && typeof a === 'object' && 'text' in a).slice(0, 5)
      : [];
  }

  get actions() {
    return this._actions;
  }

  set footerButton(value) {
    this._footerButton = value && typeof value === 'object' && 'text' in value
      ? { text: String(value.text) }
      : { text: 'Confirm' };
  }

  get footerButton() {
    return this._footerButton;
  }

  set title(value) {
    this._title = typeof value === 'string' ? value : '';
  }

  get title() {
    return this._title;
  }

  set message(value) {
    this._message = typeof value === 'string' ? value : '';
  }

  get message() {
    return this._message;
  }

  firstUpdated() {
    if (this.open) {
      this._focusInitialElement();
    }

    const overlay = this.renderRoot.querySelector('.overlay');
    const actionSheet = this.renderRoot.querySelector('.action-sheet');

    this._eventListeners = [
      { element: overlay, event: 'click', handler: (e) => this._handleOverlayClick(e) },
      { element: actionSheet, event: 'click', handler: (e) => e.stopPropagation() },
      { element: document, event: 'keydown', handler: (e) => this._handleKeydown(e) },
    ];

    this._eventListeners.forEach(({ element, event, handler }) =>
      element.addEventListener(event, handler)
    );
  }

  updated(changedProperties) {
    if (changedProperties.has('open') && this.open) {
      this._focusInitialElement();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  _focusInitialElement() {
    const focusable = this.renderRoot.querySelector('.footer-button') ||
                     this.renderRoot.querySelector('.action button');
    if (focusable) {
      focusable.focus();
    }
  }

  _handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      this.close();
    }
  }

  _handleKeydown(e) {
    if (e.key === 'Escape' && this.open) {
      e.preventDefault();
      this.close();
    }
  }

  _handleActionClick(action) {
    this.dispatchEvent(
      new CustomEvent('action-click', {
        detail: { text: action.text },
        bubbles: true,
        composed: true,
      })
    );
    this.close();
  }

  _handleFooterClick() {
    this.dispatchEvent(new CustomEvent('footer-click', { bubbles: true, composed: true }));
    this.close();
  }

  openSheet() {
    this.open = true;
  }

  close() {
    this.open = false;
  }

  render() {
    if (!this.open) return html``;

    return html`
      <div
        class="overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="${this.title ? 'action-sheet-title' : ''}"
        aria-describedby="${this.message || this.actions.length ? 'action-sheet-content' : ''}"
      >
        <div class="action-sheet">
          <slot name="header">
            ${(this.title || this.message)
              ? html`
                  <div class="header" id="action-sheet-content">
                    ${this.title
                      ? html`
                          <span class="title" id="action-sheet-title">
                            ${this.title.length > 30 ? this.title.slice(0, 30) + '...' : this.title}
                          </span>
                        `
                      : ''}
                    ${this.message ? html`<span class="message">${this.message}</span>` : ''}
                  </div>
                `
              : ''}
          </slot>
          <slot name="actions">
            ${this.actions.length > 0
              ? html`
                  <div class="actions">
                    ${this.actions.map(
                      (action, index, slicedActions) => html`
                        <div
                          class="action
                            ${index === 0 && !this.title && !this.message ? 'first' : ''}
                            ${index === slicedActions.length - 1 ? 'last' : ''}"
                        >
                          <button
                            style="color: ${action.color || 'var(--color-primary-dark, #001d68)'}"
                            @click="${() => this._handleActionClick(action)}"
                          >
                            ${action.text || 'Action'}
                          </button>
                          ${index < slicedActions.length - 1 ? html`<div class="divider"></div>` : ''}
                        </div>
                      `
                    )}
                  </div>
                `
              : ''}
          </slot>
          <slot name="footer-button">
            <button class="footer-button" @click="${this._handleFooterClick}">
              ${this.footerButton.text || 'Confirm'}
            </button>
          </slot>
          <slot></slot>
        </div>
      </div>
    `;
  }
}

customElements.define('studio-actions', StudioActions);